import jwt_decode from "jwt-decode";

// The ONE claims contract for a QLearn session token (round-2 finding 5):
// the payload must decode to an object carrying a FINITE NUMERIC `exp`
// (NumericDate) — exp is the only claim the app's own code consumes (the
// store.js fetch interceptor and the restore gates); identity rides the
// backend response fields (id/userId), never token claims. A truthy
// non-numeric exp previously slipped the interceptor's expiry math
// (Date.now() >= NaN === false) and rode every protected request.
const decodeSessionClaims = (authToken) => {
  // Three dot-separated segments; the charset is deliberately jwt-decode's
  // tolerance (base64url AND legacy base64 +/=) — STORED tokens include
  // historical shapes, and this guard must never be stricter than the
  // decoder the interceptor actually runs.
  if (typeof authToken !== 'string' ||
      !/^[A-Za-z0-9_+/=-]+\.[A-Za-z0-9_+/=-]+\.[A-Za-z0-9_+/=-]+$/.test(authToken)) {
    return null;
  }
  try {
    const payload = jwt_decode(authToken);
    if (payload === null || typeof payload !== 'object') return null;
    // exp must be a TYPED number: Number() coercion let `exp: [4102444800]`
    // and numeric strings satisfy the finite check while still not being the
    // NumericDate the interceptor's expiry math is contracted on.
    if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) return null;
    return { payload, expMs: payload.exp * 1000 };
  } catch {
    return null;
  }
};

// A stored session token is USABLE only when its claims parse (finite numeric
// exp) AND the expiry is in the future. Callers gate session-restore redirects
// on this so a stale/expired JWT renders the sign-in form instead of bouncing
// (gate leg-2 B1: userId-only redirect + the notes-to-quiz claim page's
// token-only clear composed into an infinite /login <-> claim-link loop).
export const isTokenUsable = (authToken) => {
  const claims = decodeSessionClaims(authToken);
  return claims !== null && new Date().getTime() < claims.expMs;
};

// How far a device clock may run fast or slow before a stored login is
// judged dead. Destructive callers (clearing a session on a QLink landing)
// use isTokenDeadBeyondSkew, never isTokenUsable, so a skewed clock cannot
// sign out a session the server still accepts.
export const CLOCK_SKEW_GRACE_MS = 10 * 60 * 1000;

// Dead even allowing for clock skew: no token, claims that do not parse, or
// expired more than CLOCK_SKEW_GRACE_MS ago by this device's clock.
export const isTokenDeadBeyondSkew = (authToken, now = Date.now()) => {
  const claims = decodeSessionClaims(authToken);
  return claims === null || now >= claims.expMs + CLOCK_SKEW_GRACE_MS;
};

// A JWT we are willing to PERSIST: same claims contract INCLUDING a future
// exp — persisting an already-expired or claims-broken token only manufactures
// the stale-session states the restore gates exist to fend off.
export const isTokenPersistable = (authToken) => isTokenUsable(authToken);

// Verified-email enforcement marker (GPT-5.6 finding 3): while an email-OTP
// proof is pending, this key holds the address being verified. The login
// restore refuses to redirect into the app while it is set, and the phone
// auth surface resumes the EMAIL_VERIFY screen from it on reload — otherwise
// a reload after isProfileComplete='true' was persisted (but before the OTP
// succeeded) silently bypassed the "prove email ownership" gate.
export const PENDING_EMAIL_VERIFY_KEY = 'pendingEmailVerify';

export const getPendingEmailVerify = () => {
  try {
    return window.localStorage.getItem(PENDING_EMAIL_VERIFY_KEY) || null;
  } catch {
    return null;
  }
};
